import { useState } from 'react';
import { resetPasswordWithToken } from '../api/reset-password';
import type { PasswordResetWithTokenFormData, PasswordResetError } from '../types';

interface UsePasswordResetWithTokenReturn {
  isLoading: boolean;
  isSuccess: boolean;
  successMessage: string;
  userEmail: string;
  errors: PasswordResetError;
  handlePasswordReset: (
    formData: PasswordResetWithTokenFormData,
    uidb64: string,
    token: string
  ) => Promise<void>;
  clearError: (field: string) => void;
}

export const usePasswordResetWithToken = (): UsePasswordResetWithTokenReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [errors, setErrors] = useState<PasswordResetError>({});

  const clearError = (field: string) => {
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  };

  const handlePasswordReset = async (
    formData: PasswordResetWithTokenFormData,
    uidb64: string,
    token: string
  ): Promise<void> => {
    setIsLoading(true);
    setErrors({});
    setIsSuccess(false);

    try {
      const resetPayload = {
        uidb64,
        token,
        new_password: formData.newPassword,
        confirm_password: formData.confirmPassword,
      };

      const response = await resetPasswordWithToken(resetPayload);

      // Set success state
      setIsSuccess(true);
      setSuccessMessage(response.message || 'Password reset successfully.');
      setUserEmail(response.email || '');
    } catch (error: any) {
      console.error('Password reset error:', error);

      // Handle validation errors from the API
      if (error?.data?.errors) {
        const apiErrors = error.data.errors;
        // Convert snake_case API error keys to camelCase for frontend
        const formattedErrors: PasswordResetError = {};
        
        Object.keys(apiErrors).forEach((key) => {
          // Convert snake_case to camelCase
          const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
          formattedErrors[camelKey] = apiErrors[key];
        });
        
        setErrors(formattedErrors);
      } else {
        // Handle generic errors
        const errorMessage = 
          error?.data?.error || 
          error?.message || 
          'An error occurred during password reset. Please try again.';
        setErrors({ non_field_errors: [errorMessage] });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    isSuccess,
    successMessage,
    userEmail,
    errors,
    handlePasswordReset,
    clearError,
  };
};

